<!--
  Mobile Controls - Simplified Hybrid System
  
  Test Results Table:
  | #  | Scenario                           | Layout    | Left Side                    | Right Side                           |
  |----|------------------------------------|-----------|------------------------------|--------------------------------------|
  | 1  | Map Only                          | Horizontal| Map                          | -                                    |
  | 2  | Personalization Only              | Horizontal| Personalization              | -                                    |
  | 3  | Audio Only                        | Horizontal| -                            | Audio                                |
  | 4  | Description Only                  | Horizontal| Description                  | -                                    |
  | 5  | Map + Personalization             | Horizontal| Map                          | Personalization                      |
  | 6  | Map + Audio                       | Horizontal| Map                          | Audio                                |
  | 7  | Map + Description                 | Horizontal| Map                          | Description                          |
  | 8  | Personalization + Audio           | Horizontal| Personalization              | Audio                                |
  | 9  | Personalization + Description     | Horizontal| Personalization              | Description                          |
  | 10 | Description + Audio               | Horizontal| Description                  | Audio                                |
  | 11 | Map + Personalization + Audio     | Vertical  | Map (bottom-left)            | Personalization + Audio (stack)      |
  | 12 | Map + Personalization + Description| Vertical | Map (bottom-left)            | Personalization + Description (stack)|
  | 13 | Map + Description + Audio         | Vertical  | Map (bottom-left)            | Description + Audio (stack)          |
  | 14 | Personalization + Description + Audio| Vertical| -                           | Personalization + Description + Audio (stack)|
  | 15 | All 4 Components                  | Vertical  | Map (bottom-left)            | Personalization + Description + Audio (stack)|
  
  Key Rules:
  - Center zone (201px) always preserved - no components ever centered
  - Map always left in horizontal, bottom-left in vertical
  - Audio always right in horizontal, last in vertical stack
  - Vertical stack order: Personalization → Description → Audio
  - Layout decision: useVerticalLayout = componentCount > 2
-->

<script>
  import { createEventDispatcher } from 'svelte';
  import PersonalizationButton from './PersonalizationButton.svelte';
  import MapButton from './MapButton.svelte';
  import Audio from './Audio.svelte';
  import MobileSceneDescription from './MobileSceneDescription.svelte';
  
  const dispatch = createEventDispatcher();
  
  // Props
  export let hasPersonalization = false;
  export let hasDescription = false;
  export let hasAudio = false;
  export let hasMap = false;
  export let expandedMap = false;
  export let tour = null;
  export let playing = true;
  export let loop = false;
  export let resumable = true;
  export let debug = false;
  export let enableSceneAudio = true;
  export let enableDescriptionAudio = true;
  export let onPlayingChange = null;
  export let theme = null;
  
  // Mobile detection
  import UA from "ua-parser-js";
  const ua = new UA();
  let isMobile = ua.getResult().device["type"] == "mobile";

  // Simple component availability check
  $: hasAudioInTour = hasAudio && tour?.scenes?.some(s => s.audioId || s.descriptionAudio?.assetId);
  
  // Count total available components
  $: componentCount = [hasMap, hasPersonalization, hasAudioInTour, hasDescription].filter(Boolean).length;
  
  // Determine if we should use vertical layout (3+ components)
  $: useVerticalLayout = componentCount > 2;
  
  // Event handlers
  function handlePersonalize() {
    dispatch('personalize');
  }

  function handleDescriptionOpen(e) {
    dispatch('descriptionOpen', e.detail);
  }

  function handleMapClick() {
    dispatch('mapClick');
  }


</script>

{#if isMobile}
  <div class="mobile-controls-wrapper">
    {#if useVerticalLayout}
      <!-- Map button in bottom left for vertical layout (independent positioning) -->
      {#if hasMap}
        <div class="map-bottom-left">
          <MapButton click={handleMapClick} {expandedMap} mobileOnly={true} bgColor={theme?.primaryColor} />
        </div>
      {/if}
      
      <!-- Vertical Stack Layout for other controls (3+ components) -->
      <div class="mobile-controls-vertical">
        <div class="vertical-stack">
          {#if hasPersonalization}
            <div class="control-item" class:hidden={expandedMap}>
              <PersonalizationButton on:personalize={handlePersonalize} />
            </div>
          {/if}
          
          {#if hasDescription}
            <div class="control-item" class:hidden={expandedMap}>
              <MobileSceneDescription {tour} {hasDescription} on:descriptionOpen={handleDescriptionOpen} />
            </div>
          {/if}
          
          {#if hasAudioInTour}
            <div class="control-item" class:hidden={expandedMap}>
              <Audio
                {tour}
                {playing}
                {loop}
                {resumable}
                {debug}
                {enableSceneAudio}
                {enableDescriptionAudio}
                {onPlayingChange}
                autoInit={true}
              />
            </div>
          {/if}
        </div>
      </div>
    {:else}
      <!-- Horizontal Layout for 1-2 components -->
      <div class="mobile-controls">
        <!-- Left Controls -->
        <div class="left-controls">
          {#if hasMap}
            <div class="control-container">
              <MapButton click={handleMapClick} {expandedMap} mobileOnly={true} bgColor={theme?.primaryColor} />
            </div>
          {/if}
          
          {#if hasPersonalization && !hasMap}
            <div class="control-container">
              <PersonalizationButton on:personalize={handlePersonalize} />
            </div>
          {/if}
          
          {#if hasDescription && !hasMap && !hasPersonalization}
            <div class="control-container">
              <MobileSceneDescription {tour} {hasDescription} on:descriptionOpen={handleDescriptionOpen} />
            </div>
          {/if}
        </div>
        
        <!-- Right Controls -->
        <div class="right-controls">
          {#if hasPersonalization && hasMap}
            <div class="control-container" class:hidden={expandedMap}>
              <PersonalizationButton on:personalize={handlePersonalize} />
            </div>
          {/if}
          
          {#if hasAudioInTour}
            <div class="control-container" class:hidden={expandedMap}>
              <Audio
                {tour}
                {playing}
                {loop}
                {resumable}
                {debug}
                {enableSceneAudio}
                {enableDescriptionAudio}
                {onPlayingChange}
                autoInit={true}
              />
            </div>
          {/if}
          
          {#if hasDescription && (hasMap || hasPersonalization)}
            <div class="control-container" class:hidden={expandedMap}>
              <MobileSceneDescription {tour} {hasDescription} on:descriptionOpen={handleDescriptionOpen} />
            </div>
          {/if}
        </div>
      </div>
    {/if}
  </div>
{/if}

<style>
  .mobile-controls {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 20px;
    z-index: 1000;
    pointer-events: none;
  }

  .left-controls, .right-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    pointer-events: auto;
  }

  .control-container {
    display: flex;
    align-items: center;
  }

  .control-container.hidden {
    display: none;
  }

  /* Vertical layout styles */
  .mobile-controls-vertical {
    position: fixed;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1000;
    pointer-events: none;
  }

  .vertical-stack {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 0;
    pointer-events: auto;
  }

  .control-item {
    display: flex;
    align-items: center;
  }

  .control-item.hidden {
    display: none;
  }

  .map-bottom-left {
    position: fixed;
    bottom: 0;
    left: 0;
    padding: 10px;
    z-index: 1000;
    pointer-events: auto;
  }

  /* Targeted overrides for child components within mobile controls */
  .mobile-controls-wrapper :global(.personalization-button) {
    position: static !important;
    margin: 0 !important;
  }

  .mobile-controls-wrapper :global(.container) {
    position: static !important;
    margin: 0 !important;
  }

  .mobile-controls-wrapper :global(.map-button) {
    position: static !important;
    margin: 0 !important;
  }
</style>
